/**
 * Section B5 Page
 * B.5.額外性說明
 * 四頁籤：頁籤1/2/4 各一富文本，頁籤3 多選類別+富文本，各頁籤個別儲存
 */

import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { validateSession } from '../utils/sessionValidator';
import { usePermission } from '../hooks/usePermission';
import { useProject } from '../contexts/ProjectContext';
import sectionB5Service, { SectionB5Detail, SectionB5Update } from '../services/sectionB5Service';
import RichTextEditor from '../components/RichTextEditor';
import FunctionPageHeader from '../components/FunctionPageHeader';
import { useToast } from '../contexts/ToastContext';
import '../styles/DataTable.css';

const SectionB5Page: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { hasPermission, loading: permissionLoading } = usePermission();
  const { selectedProjectId } = useProject();
  const toast = useToast();
  const hasInitialized = useRef(false);

  const [activeTab, setActiveTab] = useState<number>(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    regulatory_analysis: '',
    avoidance_lockin: '',
    financial_performance_type: [1] as number[],
    financial_performance: '',
    common_practice_analysis: ''
  });
  const [originalData, setOriginalData] = useState({ ...formData });

  const isZh = i18n.language === 'zh-TW';

  const tabs = [
    { key: 1, label: isZh ? 'B.5.1.法規分析' : 'B.5.1. Regulatory analysis' },
    { key: 2, label: isZh ? 'B.5.2.避免高碳鎖定說明' : 'B.5.2. Avoidance of lock-in' },
    { key: 3, label: isZh ? 'B.5.3.財務績效說明' : 'B.5.3. Financial/Performance' },
    { key: 4, label: isZh ? 'B.5.4.實務分析' : 'B.5.4. Common practice analysis' }
  ];

  useEffect(() => {
    if (!hasInitialized.current && !permissionLoading) {
      validateSession('SectionB5Page');
      hasInitialized.current = true;
    }
  }, [permissionLoading]);

  useEffect(() => {
    if (!selectedProjectId) {
      const empty = { regulatory_analysis: '', avoidance_lockin: '', financial_performance_type: [1], financial_performance: '', common_practice_analysis: '' };
      setFormData(empty);
      setOriginalData(empty);
      return;
    }
    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await sectionB5Service.getSectionB5ByProject(selectedProjectId);
        if (data) {
          const values = {
            regulatory_analysis: data.regulatory_analysis || '',
            avoidance_lockin: data.avoidance_lockin || '',
            financial_performance_type: data.financial_performance_type || [1],
            financial_performance: data.financial_performance || '',
            common_practice_analysis: data.common_practice_analysis || ''
          };
          setFormData(values);
          setOriginalData(values);
        } else {
          const empty = { regulatory_analysis: '', avoidance_lockin: '', financial_performance_type: [1], financial_performance: '', common_practice_analysis: '' };
          setFormData(empty);
          setOriginalData(empty);
        }
      } catch (err: any) {
        if (err.response?.status === 404 || err.response?.status === 500) {
          const empty = { regulatory_analysis: '', avoidance_lockin: '', financial_performance_type: [1], financial_performance: '', common_practice_analysis: '' };
          setFormData(empty);
          setOriginalData(empty);
        } else {
          setError(err.response?.data?.detail || (isZh ? '載入資料失敗' : 'Failed to load data'));
        }
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [selectedProjectId, isZh]);

  const handleSave = async () => {
    if (!selectedProjectId) return;

    // 頁籤3 驗證：有財務績效說明時，必須至少勾選一個類別
    if (activeTab === 3 && formData.financial_performance.trim()) {
      const hasText = formData.financial_performance.replace(/<[^>]+>/g, '').trim();
      if (hasText && formData.financial_performance_type.length === 0) {
        toast.warning(isZh ? '請至少勾選一個財務績效類別' : 'Please select at least one financial performance type');
        return;
      }
    }

    try {
      setLoading(true);
      setError(null);
      const updateData: SectionB5Update = {};
      if (activeTab === 1) updateData.regulatory_analysis = formData.regulatory_analysis || undefined;
      if (activeTab === 2) updateData.avoidance_lockin = formData.avoidance_lockin || undefined;
      if (activeTab === 3) {
        updateData.financial_performance_type = formData.financial_performance_type;
        updateData.financial_performance = formData.financial_performance || undefined;
      }
      if (activeTab === 4) updateData.common_practice_analysis = formData.common_practice_analysis || undefined;

      const updated = await sectionB5Service.updateSectionB5(selectedProjectId, updateData);
      const values = {
        regulatory_analysis: updated.regulatory_analysis || '',
        avoidance_lockin: updated.avoidance_lockin || '',
        financial_performance_type: updated.financial_performance_type || [1],
        financial_performance: updated.financial_performance || '',
        common_practice_analysis: updated.common_practice_analysis || ''
      };
      // 只更新目前頁籤的原始資料
      if (activeTab === 1) setOriginalData(prev => ({ ...prev, regulatory_analysis: values.regulatory_analysis }));
      if (activeTab === 2) setOriginalData(prev => ({ ...prev, avoidance_lockin: values.avoidance_lockin }));
      if (activeTab === 3) setOriginalData(prev => ({ ...prev, financial_performance_type: values.financial_performance_type, financial_performance: values.financial_performance }));
      if (activeTab === 4) setOriginalData(prev => ({ ...prev, common_practice_analysis: values.common_practice_analysis }));

      toast.success(isZh ? '儲存成功' : 'Saved successfully');
    } catch (err: any) {
      toast.error(err.response?.data?.detail || (isZh ? '儲存失敗' : 'Save failed'));
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    if (activeTab === 1) setFormData(prev => ({ ...prev, regulatory_analysis: originalData.regulatory_analysis }));
    if (activeTab === 2) setFormData(prev => ({ ...prev, avoidance_lockin: originalData.avoidance_lockin }));
    if (activeTab === 3) setFormData(prev => ({ ...prev, financial_performance_type: [...originalData.financial_performance_type], financial_performance: originalData.financial_performance }));
    if (activeTab === 4) setFormData(prev => ({ ...prev, common_practice_analysis: originalData.common_practice_analysis }));
    setError(null);
  };

  const toggleFinancialType = (type: number) => {
    setFormData(prev => {
      const types = prev.financial_performance_type.includes(type)
        ? prev.financial_performance_type.filter(t => t !== type)
        : [...prev.financial_performance_type, type];
      return { ...prev, financial_performance_type: types };
    });
  };

  if (permissionLoading) return <div className="page-container"><div className="loading">{t('common.loading')}</div></div>;
  if (!hasPermission('section_b5', 'read')) return <div className="page-container"><div className="error-message">{t('common.noPermission')}</div></div>;
  const canEdit = hasPermission('section_b5', 'update');

  return (
    <div className="page-container">
      <div className="page-header">
        <FunctionPageHeader funcCode="section_b5" />
      </div>

      {error && <div className="error-message" style={{ marginBottom: '16px' }}>{error}</div>}

      {selectedProjectId && (
        <div>
          <div style={{ display: 'flex', borderBottom: '2px solid #e0e0e0', marginBottom: '16px', flexWrap: 'wrap' }}>
            {tabs.map(tab => (
              <button key={tab.key} onClick={() => setActiveTab(tab.key)} style={{
                padding: '10px 16px', border: 'none',
                borderBottom: activeTab === tab.key ? '3px solid #4A90E2' : '3px solid transparent',
                background: activeTab === tab.key ? '#f0f7ff' : 'transparent',
                color: activeTab === tab.key ? '#4A90E2' : '#666',
                fontWeight: activeTab === tab.key ? 600 : 400,
                cursor: 'pointer', fontSize: '13px', marginBottom: '-2px'
              }}>
                {tab.label}
              </button>
            ))}
          </div>

          {loading ? <div className="loading">{t('common.loading')}</div> : (
            <>
              {/* 頁籤1: 法規分析 */}
              {activeTab === 1 && (
                <div style={{ marginBottom: '24px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>{isZh ? '法規分析' : 'Regulatory analysis'}</label>
                  <RichTextEditor value={formData.regulatory_analysis} onChange={html => setFormData(prev => ({ ...prev, regulatory_analysis: html }))} placeholder={isZh ? '請輸入法規分析...' : 'Please enter regulatory analysis...'} disabled={!canEdit} />
                </div>
              )}

              {/* 頁籤2: 避免高碳鎖定 */}
              {activeTab === 2 && (
                <div style={{ marginBottom: '24px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>{isZh ? '避免高碳鎖定說明' : 'Avoidance of lock-in'}</label>
                  <RichTextEditor value={formData.avoidance_lockin} onChange={html => setFormData(prev => ({ ...prev, avoidance_lockin: html }))} placeholder={isZh ? '請輸入避免高碳鎖定說明...' : 'Please enter avoidance of lock-in...'} disabled={!canEdit} />
                </div>
              )}

              {/* 頁籤3: 財務績效 */}
              {activeTab === 3 && (
                <div style={{ marginBottom: '24px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>{isZh ? '財務績效類別' : 'Financial Performance Type'}</label>
                  <div style={{ display: 'flex', gap: '24px', marginBottom: '16px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: canEdit ? 'pointer' : 'default' }}>
                      <input type="checkbox" checked={formData.financial_performance_type.includes(1)} onChange={() => canEdit && toggleFinancialType(1)} disabled={!canEdit} style={{ width: '18px', height: '18px' }} />
                      {isZh ? '財務額外說明 (Financial additionality)' : 'Financial additionality'}
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: canEdit ? 'pointer' : 'default' }}>
                      <input type="checkbox" checked={formData.financial_performance_type.includes(2)} onChange={() => canEdit && toggleFinancialType(2)} disabled={!canEdit} style={{ width: '18px', height: '18px' }} />
                      {isZh ? '績效基準 (Performance-based approach)' : 'Performance-based approach'}
                    </label>
                  </div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>{isZh ? '財務績效說明' : 'Financial Performance Description'}</label>
                  <RichTextEditor value={formData.financial_performance} onChange={html => setFormData(prev => ({ ...prev, financial_performance: html }))} placeholder={isZh ? '請輸入財務績效說明...' : 'Please enter financial performance description...'} disabled={!canEdit} />
                </div>
              )}

              {/* 頁籤4: 實務分析 */}
              {activeTab === 4 && (
                <div style={{ marginBottom: '24px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>{isZh ? '實務分析' : 'Common practice analysis'}</label>
                  <RichTextEditor value={formData.common_practice_analysis} onChange={html => setFormData(prev => ({ ...prev, common_practice_analysis: html }))} placeholder={isZh ? '請輸入實務分析...' : 'Please enter common practice analysis...'} disabled={!canEdit} />
                </div>
              )}

              {canEdit && (
                <div style={{ display: 'flex', justifyContent: 'center', gap: '12px', marginTop: '24px' }}>
                  <button className="btn-primary" onClick={handleSave} disabled={loading}>{loading ? t('message.saving') : t('common.save')}</button>
                  <button className="btn-secondary" onClick={handleReset} disabled={loading}>{t('common.reset')}</button>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {!selectedProjectId && !loading && (
        <div style={{ padding: '48px', textAlign: 'center', color: '#999', fontSize: '16px' }}>
          {isZh ? '請先在上方選擇專案' : 'Please select a project from the top bar'}
        </div>
      )}
    </div>
  );
};

export default SectionB5Page;
